import React from "react"
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Chip,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField
} from "@mui/material"
import { Delete as DeleteIcon, Add as AddIcon } from "@mui/icons-material"
import { t } from "@lingui/macro"
import { ErrorResponse, Loading, RestResponse } from "@sinossi/mates-react-library"
import { 
    GeographicPermission, 
    GeographicPermissionRequest, 
    GeographicPermissionType 
} from "models"
import { GeographicPermissionService } from "services/geographic-permission.service"

interface GeographicPermissionModalProps {
    open: boolean
    accountId: string
    onClose: () => void
}

interface GeographicPermissionModalState {
    permissions: GeographicPermission[]
    loading: boolean
    showAddForm: boolean
    newPermission: GeographicPermissionRequest
}

export default class GeographicPermissionModal extends React.Component<
    GeographicPermissionModalProps,
    GeographicPermissionModalState
> {
    constructor(props: GeographicPermissionModalProps) {
        super(props)
        this.state = {
            permissions: [],
            loading: true,
            showAddForm: false,
            newPermission: {
                permissionType: GeographicPermissionType.REGION,
                region: "",
                province: "",
                city: ""
            }
        }
    }

    componentDidMount() {
        if (this.props.open) {
            this.loadPermissions()
        }
    }

    componentDidUpdate(prevProps: GeographicPermissionModalProps) {
        if (this.props.open && !prevProps.open) {
            this.loadPermissions()
        }
    }

    private loadPermissions = () => {
        this.setState({ loading: true })
        GeographicPermissionService.getPermissions(
            this.props.accountId,
            this.onPermissionsLoaded,
            this.onError
        )
    }

    private onPermissionsLoaded = (response: RestResponse<GeographicPermission[], ErrorResponse>) => {
        if (response.hasError()) {
            console.error("Error loading permissions:", response.error)
            this.setState({ loading: false })
            return
        }

        this.setState({
            permissions: response.data || [],
            loading: false
        })
    }

    private onError = (error: any) => {
        console.error("Error:", error)
        this.setState({ loading: false })
    }

    private removePermission = (permissionId: string) => {
        this.setState({ loading: true })
        GeographicPermissionService.removePermission(
            this.props.accountId,
            permissionId,
            this.onPermissionRemoved,
            this.onError
        )
    }

    private onPermissionRemoved = (response: RestResponse<any, ErrorResponse>) => {
        if (response.hasError()) {
            console.error("Error removing permission:", response.error)
            this.setState({ loading: false })
            return
        }

        this.loadPermissions()
    }

    private addPermission = () => {
        const { newPermission } = this.state
        
        // Validazione
        if (newPermission.permissionType === GeographicPermissionType.REGION && !newPermission.region?.trim()) {
            return
        }
        if (newPermission.permissionType === GeographicPermissionType.PROVINCE && !newPermission.province?.trim()) {
            return
        }
        if (newPermission.permissionType === GeographicPermissionType.CITY && !newPermission.city?.trim()) {
            return
        }

        this.setState({ loading: true })
        GeographicPermissionService.addPermission(
            this.props.accountId,
            newPermission,
            this.onPermissionAdded,
            this.onError
        )
    }

    private onPermissionAdded = (response: RestResponse<any, ErrorResponse>) => {
        if (response.hasError()) {
            console.error("Error adding permission:", response.error)
            this.setState({ loading: false })
            return
        }

        this.setState({
            showAddForm: false,
            newPermission: {
                permissionType: GeographicPermissionType.REGION,
                region: "",
                province: "",
                city: ""
            }
        })
        this.loadPermissions()
    }

    private getPermissionLabel = (permission: GeographicPermission): string => {
        switch (permission.permissionType) {
            case GeographicPermissionType.REGION:
                return `Regione: ${permission.region}`
            case GeographicPermissionType.PROVINCE:
                return `Provincia: ${permission.province}`
            case GeographicPermissionType.CITY:
                return `Città: ${permission.city}`
            default:
                return ""
        }
    }

    private getPermissionColor = (type: GeographicPermissionType): "primary" | "secondary" | "success" => {
        switch (type) {
            case GeographicPermissionType.REGION:
                return "primary"
            case GeographicPermissionType.PROVINCE:
                return "secondary"
            case GeographicPermissionType.CITY:
                return "success"
            default:
                return "primary"
        }
    }

    private getCurrentFieldValue = (): string => {
        const { newPermission } = this.state
        switch (newPermission.permissionType) {
            case GeographicPermissionType.REGION:
                return newPermission.region || ""
            case GeographicPermissionType.PROVINCE:
                return newPermission.province || ""
            case GeographicPermissionType.CITY:
                return newPermission.city || ""
            default:
                return ""
        }
    }

    private updateCurrentField = (value: string) => {
        const { newPermission } = this.state
        const updatedPermission = { ...newPermission }
        
        switch (newPermission.permissionType) {
            case GeographicPermissionType.REGION:
                updatedPermission.region = value
                updatedPermission.province = ""
                updatedPermission.city = ""
                break
            case GeographicPermissionType.PROVINCE:
                updatedPermission.province = value
                updatedPermission.region = ""
                updatedPermission.city = ""
                break
            case GeographicPermissionType.CITY:
                updatedPermission.city = value
                updatedPermission.region = ""
                updatedPermission.province = ""
                break
        }
        
        this.setState({ newPermission: updatedPermission })
    }

    render() {
        const { open, onClose } = this.props
        const { permissions, loading, showAddForm, newPermission } = this.state

        return (
            <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
                <DialogTitle>
                    {t({ id: "geographic-permissions.title", message: "Permessi Geografici" })}
                </DialogTitle>
                <DialogContent>
                    <Loading show={loading} />
                    
                    {/* Lista permessi esistenti */}
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="h6" sx={{ mb: 2 }}>
                            {t({ id: "geographic-permissions.current", message: "Permessi Attuali" })}
                        </Typography>
                        {permissions.length === 0 ? (
                            <Typography color="text.secondary">
                                {t({ id: "geographic-permissions.no-permissions", message: "Nessun permesso geografico assegnato" })}
                            </Typography>
                        ) : (
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                                {permissions.map((permission) => (
                                    <Chip
                                        key={permission.id}
                                        label={this.getPermissionLabel(permission)}
                                        color={this.getPermissionColor(permission.permissionType)}
                                        onDelete={() => this.removePermission(permission.id)}
                                        deleteIcon={<DeleteIcon />}
                                        variant="outlined"
                                    />
                                ))}
                            </Box>
                        )}
                    </Box>

                    {/* Pulsante per aggiungere nuovo permesso */}
                    {!showAddForm && (
                        <Button
                            startIcon={<AddIcon />}
                            onClick={() => this.setState({ showAddForm: true })}
                            variant="outlined"
                            sx={{ mb: 2 }}
                        >
                            {t({ id: "geographic-permissions.add", message: "Aggiungi Permesso" })}
                        </Button>
                    )}

                    {/* Form per aggiungere nuovo permesso */}
                    {showAddForm && (
                        <Box sx={{ mt: 2, p: 2, border: "1px solid #e0e0e0", borderRadius: 1 }}>
                            <Typography variant="h6" sx={{ mb: 2 }}>
                                {t({ id: "geographic-permissions.add-new", message: "Aggiungi Nuovo Permesso" })}
                            </Typography>
                            
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                <FormControl fullWidth>
                                    <InputLabel>Tipo Permesso</InputLabel>
                                    <Select
                                        value={newPermission.permissionType}
                                        label="Tipo Permesso"
                                        onChange={(e) => this.setState({
                                            newPermission: {
                                                ...newPermission,
                                                permissionType: e.target.value as GeographicPermissionType,
                                                region: "",
                                                province: "",
                                                city: ""
                                            }
                                        })}
                                    >
                                        <MenuItem value={GeographicPermissionType.REGION}>Regione</MenuItem>
                                        <MenuItem value={GeographicPermissionType.PROVINCE}>Provincia</MenuItem>
                                        <MenuItem value={GeographicPermissionType.CITY}>Città</MenuItem>
                                    </Select>
                                </FormControl>

                                <TextField
                                    fullWidth
                                    label={
                                        newPermission.permissionType === GeographicPermissionType.REGION ? "Nome Regione" :
                                        newPermission.permissionType === GeographicPermissionType.PROVINCE ? "Nome Provincia" :
                                        "Nome Città"
                                    }
                                    placeholder={
                                        newPermission.permissionType === GeographicPermissionType.REGION ? "es. Lombardia" :
                                        newPermission.permissionType === GeographicPermissionType.PROVINCE ? "es. Milano" :
                                        "es. Roma"
                                    }
                                    value={this.getCurrentFieldValue()}
                                    onChange={(e) => this.updateCurrentField(e.target.value)}
                                />

                                <Box sx={{ display: "flex", gap: 1 }}>
                                    <Button
                                        variant="contained"
                                        onClick={this.addPermission}
                                        disabled={!this.getCurrentFieldValue().trim()}
                                    >
                                        {t({ id: "geographic-permissions.save", message: "Salva" })}
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        onClick={() => this.setState({ showAddForm: false })}
                                    >
                                        {t({ id: "geographic-permissions.cancel", message: "Annulla" })}
                                    </Button>
                                </Box>
                            </Box>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose}>
                        {t({ id: "geographic-permissions.close", message: "Chiudi" })}
                    </Button>
                </DialogActions>
            </Dialog>
        )
    }
}
